/**
 * Toast Notification Utility
 *
 * Single minimalist toast system: dark grey surface, Iconify icon accent,
 * top-right placement, newest toast stacks ON TOP of older ones.
 *
 * All toasts funnel through here — the global <Toaster> in index.tsx shares
 * TOAST_BASE_STYLE, so raw `toast.success(...)` calls elsewhere inherit the
 * same look. A short-window message registry suppresses duplicates when the
 * same logical event reaches the toast layer through two channels (e.g. the
 * action tracker's optimistic toast AND the Brainiacs Core WebSocket pushing
 * the tracker's own notification back down moments later).
 */

import toast, { Toast } from 'react-hot-toast';
import React from 'react';
import { Icon } from '@iconify/react';
import { STATUS_MAP } from './statusMapping';

export const TOAST_POSITION = 'top-right' as const;

/** Shared surface style — also applied as the <Toaster> default in index.tsx. */
export const TOAST_BASE_STYLE: React.CSSProperties = {
  background: '#1b1b1f',
  color: '#ececf1',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: '12px',
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: 1.45,
  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.45)',
};

const REMOVE_DELAY = 400; // keep mounted briefly so the exit transition plays

/** Icon carries the semantic; the surface stays neutral dark grey. */
const ACCENT = {
  success: '#34d399',
  error: '#f87171',
  info: '#818cf8',
  bell: '#a5b4fc',
  warning: '#fb923c',
} as const;

const toastIcon = (icon: string, color: string) =>
  React.createElement(Icon, { icon, style: { color, fontSize: '18px', flexShrink: 0 } });

// ---------------------------------------------------------------------------
// Duplicate suppression
// ---------------------------------------------------------------------------

/**
 * The same event can hit the toast layer twice by design: the action tracker
 * toasts optimistically, then the Core WebSocket pushes the tracker's own
 * notification back down moments later. Same-message toasts inside this
 * window are dropped.
 */
const DEDUPE_WINDOW_MS = 35_000;
const recentMessages = new Map<string, number>();

const wasRecentlyShown = (message: string): boolean => {
  const now = Date.now();
  for (const [msg, shownAt] of recentMessages) {
    if (now - shownAt > DEDUPE_WINDOW_MS) recentMessages.delete(msg);
  }
  return recentMessages.has(message);
};

const markShown = (message: string) => {
  recentMessages.set(message, Date.now());
  // Hard cap so a very chatty session can't grow the map unbounded.
  if (recentMessages.size > 100) {
    const oldest = recentMessages.keys().next().value;
    if (oldest !== undefined) recentMessages.delete(oldest);
  }
};

// ---------------------------------------------------------------------------
// Standard toasts (built-in renderer + Iconify icon)
// ---------------------------------------------------------------------------

/** Success toast notification */
export const showSuccessToast = (message: string) => {
  if (wasRecentlyShown(message)) return;
  markShown(message);
  toast.success(message, {
    duration: 3500,
    position: TOAST_POSITION,
    removeDelay: REMOVE_DELAY,
    style: TOAST_BASE_STYLE,
    icon: toastIcon('bx:bx-check-circle', ACCENT.success),
  });
};

/** Error toast notification */
export const showErrorToast = (message: string) => {
  if (wasRecentlyShown(message)) return;
  markShown(message);
  toast.error(message, {
    duration: 6000,
    position: TOAST_POSITION,
    removeDelay: REMOVE_DELAY,
    style: TOAST_BASE_STYLE,
    icon: toastIcon('bx:bx-x-circle', ACCENT.error),
  });
};

/** Info toast notification */
export const showInfoToast = (message: string) => {
  if (wasRecentlyShown(message)) return;
  markShown(message);
  toast(message, {
    duration: 4000,
    position: TOAST_POSITION,
    removeDelay: REMOVE_DELAY,
    style: TOAST_BASE_STYLE,
    icon: toastIcon('bx:bx-info-circle', ACCENT.info),
  });
};

/**
 * Loading toast notification
 * Returns toast ID for dismissal
 */
export const showLoadingToast = (message: string): string => {
  return toast.loading(message, {
    position: TOAST_POSITION,
    removeDelay: REMOVE_DELAY,
    style: TOAST_BASE_STYLE,
  });
};

// ---------------------------------------------------------------------------
// Custom toasts (minimal card renderer: click to dismiss, slides in from right)
// ---------------------------------------------------------------------------

interface MinimalToastVisual {
  icon: string;
  color: string;
}

const renderMinimalToast = (message: string, { icon, color }: MinimalToastVisual) => (t: Toast) =>
  React.createElement(
    'div',
    {
      onClick: () => toast.dismiss(t.id),
      style: {
        ...TOAST_BASE_STYLE,
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        maxWidth: '360px',
        cursor: 'pointer',
        pointerEvents: 'auto' as const,
        opacity: t.visible ? 1 : 0,
        transform: t.visible
          ? 'translateX(0) scale(1)'
          : 'translateX(calc(100% + 24px)) scale(0.97)',
        transition: 'transform 380ms cubic-bezier(0.21, 1.02, 0.73, 1), opacity 280ms ease',
      },
      'aria-label': 'Dismiss notification',
    },
    toastIcon(icon, color),
    React.createElement('span', null, message),
  );

/**
 * Action-start toast (enrich) — fired optimistically by the action tracker.
 * The bolt icon uses the ENRICHING status accent (STATUS_MAP is the single
 * source of truth) so the toast visually connects to the status pill the lead
 * just switched to. Returns the toast ID so failures (e.g. insufficient
 * credits) can dismiss it.
 */
export const showEnrichToast = (message: string): string => {
  if (wasRecentlyShown(message)) return '';
  markShown(message);
  return toast.custom(renderMinimalToast(message, { icon: 'bx:bx-bolt-circle', color: STATUS_MAP.enriching.color }), {
    duration: 5000,
    position: TOAST_POSITION,
    removeDelay: REMOVE_DELAY,
  });
};

/**
 * Dismiss a specific enrich toast
 */
export const dismissEnrichToast = (toastId: string) => {
  toast.dismiss(toastId);
};

/** Async notification delivered by the long-poll (completions, bell events). */
export const showNotificationToast = (message: string) => {
  if (wasRecentlyShown(message)) return;
  markShown(message);
  toast.custom(renderMinimalToast(message, { icon: 'bx:bx-bell', color: ACCENT.bell }), {
    duration: 6000,
    position: TOAST_POSITION,
    removeDelay: REMOVE_DELAY,
  });
};

/**
 * Warning toast shown when n8n's credit checker rejects an action because the
 * client lacks enough credits. Longer-lived than the rest so the user has time
 * to read the numbers before the Buy Credits modal takes focus.
 * Returns the toast id so callers can chain dismissals (e.g. drop the blue
 * action toast that fired before the request went out).
 */
export const showInsufficientCreditsToast = (message: string): string => {
  return toast.custom(renderMinimalToast(message, { icon: 'bx:bx-error-circle', color: ACCENT.warning }), {
    duration: 8000,
    position: TOAST_POSITION,
    removeDelay: REMOVE_DELAY,
  });
};

/**
 * Dismiss a specific toast by ID
 */
export const dismissToast = (toastId: string) => {
  toast.dismiss(toastId);
};

/**
 * Dismiss all toasts
 */
export const dismissAllToasts = () => {
  toast.dismiss();
};
